interface DrawImageCanvas {
  canvasContext: CanvasRenderingContext2D;
  image: CanvasImageSource;
  imageX?: number;
  imageY?: number;
  imageWidth: number;
  imageHeight: number;
  canvasX: number;
  canvasY: number;
  canvasWidth: number;
  canvasHeight: number;
}

/**
 * Prefer desynchronized 2d contexts except on Android, where they can worsen
 * compositor / video stacking glitches.
 */
export const shouldDesynchronizeCanvas = (userAgent?: string) => {
  const resolvedUserAgent =
    userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);

  return resolvedUserAgent.length > 0 && !/Android/i.test(resolvedUserAgent);
};

/**
 * Draws an image onto a canvas with error handling.
 *
 * @param {CanvasRenderingContext2D} canvasContext - The rendering context of the canvas.
 * @param {CanvasImageSource} image - The image to be drawn onto the canvas.
 * @param {number} imageX - The x-axis coordinate of the top-left corner of the sub-rectangle of the source image to draw into the destination context.
 * @param {number} imageY - The y-axis coordinate of the top-left corner of the sub-rectangle of the source image to draw into the destination context.
 * @param {number} imageWidth - The width of the sub-rectangle of the source image to draw into the destination context.
 * @param {number} imageHeight - The height of the sub-rectangle of the source image to draw into the destination context.
 * @param {number} canvasX - The x-axis coordinate in the destination canvas at which to place the top-left corner of the source image.
 * @param {number} canvasY - The y-axis coordinate in the destination canvas at which to place the top-left corner of the source image.
 * @param {number} canvasWidth - The width to draw the image in the destination canvas.
 * @param {number} canvasHeight - The height to draw the image in the destination canvas.
 */
export const drawImageWithTryCatch = ({
  canvasContext,
  image,
  imageX = 0,
  imageY = 0,
  imageWidth,
  imageHeight,
  canvasX,
  canvasY,
  canvasWidth,
  canvasHeight,
}: DrawImageCanvas) => {
  try {
    canvasContext.drawImage(
      image,
      imageX,
      imageY,
      imageWidth,
      imageHeight,
      canvasX,
      canvasY,
      canvasWidth,
      canvasHeight
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Error canvas.drawImage:', error);
  }
};

export const getAspectRatioValue = (width: number, height: number) => {
  return width / height;
};

export const getScaledRatioSize = ({
  canvasAspectRatio,
  aspectRatio,
  width,
  height,
  ratioBy = 'responsive',
}: {
  canvasAspectRatio: number;
  aspectRatio: number;
  width: number;
  height: number;
  ratioBy?: 'responsive' | 'width' | 'height';
}) => {
  if (ratioBy === 'height') {
    return {
      width: height * aspectRatio,
      height,
    };
  }
  if (ratioBy === 'width') {
    return {
      width,
      height: width / aspectRatio,
    };
  }
  return canvasAspectRatio <= aspectRatio
    ? {
        width: height * aspectRatio,
        height,
      }
    : {
        width,
        height: width / aspectRatio,
      };
};
